import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  PageHead,
  Panel,
  PillGroup,
  Skeleton,
  StatCard,
  useToast,
} from "@guestpilot/ui-kit";
import {
  ArrowSync16Regular,
  Clock20Regular,
  People20Regular,
  PersonDelete20Regular,
  ShieldKeyhole20Regular,
} from "@fluentui/react-icons";
import { ApiProblemError } from "../../api/client";
import { useGet, useWrite } from "../../api/hooks";
import type { ExportJob, Schema, Team } from "../../api/types";
import { ParticipantsTable, type Participant } from "../../components/Participants";
import { useHandleApiError } from "../../errors";
import { formatDate } from "../../format";
import { useI18n } from "../../i18n";
import { useSession } from "../../session";
import { api } from "../../api/client";
import { DecisionForm, type Decision } from "./DecisionForm";

interface Overview {
  team: Team;
  awaitingDecision: number;
  removalInProgress: number;
  exceptionsActive: number;
  nextReviewDue?: string | null;
  lastCompletedCampaignAt?: string | null;
  preview: Participant[];
}

interface ScanRow {
  startedAt: string;
  finishedAt: string | null;
}

/** A queued scan has no run row until the worker picks it up; give up waiting after this long. */
const SCAN_WAIT_MS = 3 * 60_000;

/** Guest overview: what is happening with external access in this Team (team-tab.md). */
export function Overview({ team }: { team: Team }) {
  const { t, locale } = useI18n();
  const s = useSession();
  const nav = useNavigate();
  const toast = useToast();
  const fail = useHandleApiError();
  const qc = useQueryClient();
  const tz = s.me.tenant.timeZone;
  const q = useGet<Overview>(`/api/v1/teams/${team.id}/overview`);
  const scan = useWrite<ScanRow, never>("POST", "/api/v1/teams/:id/scans", [`/api/v1/teams`]);
  // the scan this tab asked for (server time); it is over once a run started after it has finished
  const [requestedAt, setRequestedAt] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const wasScanning = useRef(false);
  const runs = useGet<ScanRow[]>(
    `/api/v1/teams/${team.id}/scans`,
    { limit: 1 },
    {
      refetchInterval: scanning ? 2_000 : false,
    },
  );
  const latest = runs.data?.[0];
  const running = latest !== undefined && latest.finishedAt === null;
  const asked =
    requestedAt !== null &&
    !(latest?.finishedAt && Date.parse(latest.startedAt) >= Date.parse(requestedAt) - 1_000);
  const busy = running || asked;
  useEffect(() => {
    setScanning(busy);
    if (busy) {
      wasScanning.current = true;
      if (!asked) return;
      const timer = setTimeout(() => setRequestedAt(null), SCAN_WAIT_MS);
      return () => clearTimeout(timer);
    }
    if (wasScanning.current) {
      // the scan is over: the overview, the guests and the counters are read again
      wasScanning.current = false;
      setRequestedAt(null);
      void qc.invalidateQueries({
        predicate: (x) =>
          typeof x.queryKey[0] === "string" &&
          ["/api/v1/teams", "/api/v1/participants", "/api/v1/dashboard"].some((p) =>
            (x.queryKey[0] as string).startsWith(p),
          ),
      });
      toast.success(t("ui:overview.checkDone"));
    }
    return undefined;
  }, [busy, asked, qc, toast, t]);
  const [exportOpen, setExportOpen] = useState(false);
  const [adhoc, setAdhoc] = useState<Participant | null>(null);
  const o = q.data;
  if (q.isLoading) return <Skeleton rows={5} />;
  if (!o) return <EmptyState title={t("errors:not_found")} />;
  return (
    <div className="gp-page">
      <PageHead
        title={t("ui:overview.title")}
        subtitle={t("ui:overview.lead")}
        actions={
          <>
            <Button
              disabledReason={
                s.gate("TEAM_SCAN", team.id) ??
                (busy || scan.isPending ? t("ui:overview.checkingHint") : undefined)
              }
              aria-busy={busy || scan.isPending}
              onClick={() =>
                scan.mutate(
                  { params: { id: team.id } },
                  {
                    onSuccess: (r) => {
                      setRequestedAt(r.startedAt);
                      toast.success(t("ui:teamDetail.scanQueued"));
                    },
                    onError: (e) =>
                      e instanceof ApiProblemError && (e.status === 409 || e.status === 422)
                        ? toast.error(t("ui:teamDetail.scanWait"))
                        : fail(e),
                  },
                )
              }
            >
              {busy || scan.isPending ? (
                <span className="gp-spinner" aria-hidden />
              ) : (
                <ArrowSync16Regular aria-hidden />
              )}{" "}
              {busy || scan.isPending ? t("ui:overview.checking") : t("ui:overview.check")}
            </Button>
            <Button
              onClick={() => {
                void q.refetch();
                void runs.refetch();
              }}
              disabledReason={q.isFetching ? t("ui:overview.refreshing") : undefined}
            >
              {q.isFetching ? (
                <span className="gp-spinner" aria-hidden />
              ) : (
                <ArrowSync16Regular aria-hidden />
              )}{" "}
              {t("ui:overview.refresh")}
            </Button>
            <Button
              onClick={() => setExportOpen(true)}
              disabledReason={s.can("EXPORT_CREATE", team.id) ? undefined : t("ui:gate.noPermission")}
            >
              {t("ui:overview.export")}
            </Button>
          </>
        }
      />
      <div className="gp-grid-4">
        <StatCard
          icon={<People20Regular />}
          label={t("ui:overview.users")}
          value={o.team.externalUserCount ?? 0}
        />
        <StatCard
          attention={o.awaitingDecision > 0}
          icon={<Clock20Regular />}
          label={t("ui:overview.awaiting")}
          value={o.awaitingDecision}
        />
        <StatCard
          icon={<PersonDelete20Regular />}
          label={t("ui:overview.removals")}
          value={o.removalInProgress}
        />
        <StatCard
          icon={<ShieldKeyhole20Regular />}
          label={t("ui:overview.exceptions")}
          value={o.exceptionsActive}
        />
      </div>
      {o.team.activeCampaignId && o.awaitingDecision > 0 ? (
        <Banner
          tone="info"
          action={
            <Button variant="primary" onClick={() => nav(`/review/${o.team.activeCampaignId}`)}>
              {t("ui:overview.startReview")}
            </Button>
          }
        >
          {t("ui:overview.awaitingBanner", { n: o.awaitingDecision })}
        </Banner>
      ) : null}
      <Banner tone="info">
        {o.nextReviewDue
          ? t("ui:overview.next", {
              date: formatDate(o.nextReviewDue, locale, tz),
              last: formatDate(o.lastCompletedCampaignAt, locale, tz),
            })
          : t("ui:overview.noNext")}
      </Banner>
      <Panel label={t("ui:users.title")}>
        <ParticipantsTable
          rows={(o.preview ?? []).slice(0, 5)}
          onEdit={s.can("ADHOC_RAISE", team.id) ? setAdhoc : undefined}
          editLabel={t("ui:adhoc.action")}
        />
      </Panel>
      <ExportDialog open={exportOpen} team={team} onClose={() => setExportOpen(false)} />
      <AdHocDialog participant={adhoc} teamId={team.id} onClose={() => setAdhoc(null)} />
    </div>
  );
}

/** Ad-hoc decision outside any campaign (BR-208): the same form, saved as an ad-hoc review item. */
function AdHocDialog({
  participant,
  teamId,
  onClose,
}: {
  participant: Participant | null;
  teamId: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const s = useSession();
  const fail = useHandleApiError();
  const m = useWrite<unknown, Decision & { participantVersion?: number }>(
    "POST",
    "/api/v1/participants/:id/decisions",
    ["/api/v1/teams", "/api/v1/participants", "/api/v1/dashboard", "/api/v1/removals", "/api/v1/exceptions"],
  );
  if (!participant) return null;
  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={t("ui:adhoc.title")}
      description={t("ui:adhoc.lead")}
      footer={null}
    >
      <DecisionForm
        participant={participant}
        participantVersion={participant.version}
        busy={m.isPending}
        gateReason={s.gate("ADHOC_RAISE", teamId)}
        onSubmit={(d) =>
          m.mutate({ params: { id: participant.id }, body: d }, { onSuccess: onClose, onError: fail })
        }
      />
    </Dialog>
  );
}

/** Evidence export of this Team: PDF by default, CSV or XLSX; the summary names what is included (team-tab.md). */
export function ExportDialog({
  open,
  team,
  onClose,
  campaignId,
}: {
  open: boolean;
  team: Team;
  onClose: () => void;
  campaignId?: string | undefined;
}) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const fail = useHandleApiError();
  const [format, setFormat] = useState("pdf");
  const create = useWrite<ExportJob, Schema<"ExportCreate">>("POST", "/api/v1/exports", ["/api/v1/exports"]);
  const campaign = useGet<Schema<"Campaign">>(
    `/api/v1/campaigns/${campaignId ?? team.activeCampaignId ?? "none"}`,
    undefined,
    { enabled: open && Boolean(campaignId ?? team.activeCampaignId) },
  );
  const body: Schema<"ExportCreate"> = {
    scope: "team",
    teamId: team.id,
    format: format as Schema<"ExportCreate">["format"],
    language: locale,
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("ui:evidence.title")}
      description={t("ui:evidence.lead")}
      footer={
        <>
          <Button onClick={onClose}>{t("common:cancel")}</Button>
          <Button
            variant="primary"
            onClick={() =>
              create.mutate(
                { body },
                {
                  onSuccess: async (job) => {
                    onClose();
                    toast.success(t("ui:exports.queued"));
                    void pollAndOpen(job.id ?? "").then((r) => {
                      if (r !== "ready") toast.error(t("ui:exports.failedShort"));
                    });
                  },
                  onError: fail,
                },
              )
            }
          >
            {t("ui:evidence.download", { format: format.toUpperCase() })}
          </Button>
        </>
      }
    >
      <PillGroup
        legend={t("ui:exports.col.format")}
        name="evidence-format"
        value={format}
        onChange={setFormat}
        options={[
          { value: "pdf", label: t("ui:exports.pdf") },
          { value: "csv", label: "CSV" },
          { value: "xlsx", label: "XLSX" },
        ]}
      />
      <p className="t-body gp-muted" style={{ margin: 0 }}>
        {t("ui:evidence.includes", {
          n: team.externalUserCount ?? 0,
          removals: campaign.data?.resultCounts?.removalRequested ?? 0,
          exceptions: campaign.data?.resultCounts?.exceptions ?? 0,
        })}
      </p>
    </Dialog>
  );
}

/** The file is built by a background job: the download starts as soon as it is ready (BR-124, BR-225). */
async function pollAndOpen(id: string): Promise<"ready" | "failed" | "timeout"> {
  for (let i = 0; i < 20; i++) {
    await new Promise((r) => setTimeout(r, 1500));
    const j = await api.get<ExportJob>(`/api/v1/exports/${id}`).catch(() => null);
    if (j?.status === "ready") {
      const link = await api
        .post<Schema<"SignedLink">>(`/api/v1/exports/${id}/download-link`)
        .catch(() => null);
      if (link?.url) window.open(link.url, "_blank", "noopener");
      return "ready";
    }
    if (j?.status === "failed") return "failed";
  }
  return "timeout";
}
